import * as React from 'react';
import { Button as FlowBiteButton, ButtonProps as FlowBiteButtonProps } from 'flowbite-react';
import { ButtonColorsEnum, ButtonSizesEnum, buttonTheme } from '../../styles/themes/button';
import { Text, type TextVariant } from '../text';

// 'children' is omitted because this component would silently DROP them: the flowbite children slot is
// filled by `startIcon` / `label` / `endIcon` below, so `<Button>text</Button>` renders an empty pill.
// Omitting the prop turns that mistake into a compile error instead.
export type ButtonProps = Omit<FlowBiteButtonProps, 'label' | 'size' | 'color' | 'children'> & {
    label?: string;
    labelVariant?: TextVariant;
    size: ButtonSizesEnum;
    color: ButtonColorsEnum;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    ariaLabel?: string;
    testId?: string;
    isSelected?: boolean;
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
    const {
        label,
        labelVariant = 'label-medium',
        startIcon,
        endIcon,
        testId,
        isSelected,
        ariaLabel,
        ...otherProps
    } = props;

    return (
        <FlowBiteButton
            ref={ref} // needed when used as custom renderTrigger in a Dropdown
            theme={buttonTheme}
            data-testid={testId}
            data-selected={isSelected ? 'true' : undefined}
            aria-label={ariaLabel}
            {...otherProps}
        >
            {startIcon ? startIcon : null}
            {!!label ? <Text variant={labelVariant}>{label}</Text> : null}
            {endIcon ? endIcon : null}
        </FlowBiteButton>
    );
});

Button.displayName = 'Button';
